import React, { useMemo } from 'react';
import { Table, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { AttendanceRow } from '../../../services/p9315Api';
import { buildDayHoursMap, getWeekdayLabel } from '../helpers';

interface ResultTableProps {
  data: AttendanceRow[];
  loading: boolean;
  queried: boolean;
  noData: boolean;
  daysInMonth: number;
  queryYear: number;
  queryMonth: number;
}

const ResultTable: React.FC<ResultTableProps> = React.memo(({
  data, loading, queried, noData, daysInMonth, queryYear, queryMonth,
}) => {
  const columns = useMemo((): ColumnsType<AttendanceRow> => {
    // Fixed left columns
    const fixedCols: ColumnsType<AttendanceRow> = [
      {
        title: '部門',
        dataIndex: 'F910_18',
        key: 'F910_18',
        width: 60,
        fixed: 'left',
      },
      {
        title: '部門名稱',
        dataIndex: 'dept_name',
        key: 'dept_name',
        width: 100,
        fixed: 'left',
        ellipsis: true,
      },
      {
        title: '員工代號',
        dataIndex: 'F910_01',
        key: 'F910_01',
        width: 90,
        fixed: 'left',
      },
      {
        title: '姓名',
        dataIndex: 'F901_11',
        key: 'F901_11',
        width: 80,
        fixed: 'left',
      },
    ];

    // Dynamic day columns (1 ~ daysInMonth)
    const dayCols: ColumnsType<AttendanceRow> = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const weekday = getWeekdayLabel(queryYear, queryMonth, d);
      const isWeekend = weekday === '六' || weekday === '日';
      dayCols.push({
        title: (
          <div style={{ textAlign: 'center', lineHeight: '1.2' }}>
            <div>{d}</div>
            <div style={{ fontSize: 10, color: isWeekend ? '#ff4d4f' : '#999' }}>{weekday}</div>
          </div>
        ),
        key: `day_${d}`,
        width: 50,
        align: 'right',
        className: isWeekend ? 'p9315-weekend-col' : undefined,
        render: (_: unknown, record: AttendanceRow) => {
          const map = buildDayHoursMap(record.dailyHours);
          const hours = map[d];
          if (!hours || hours === 0) return '';
          return hours.toFixed(1);
        },
      });
    }

    // Summary columns (fixed right)
    const summaryCols: ColumnsType<AttendanceRow> = [
      {
        title: '合計時數',
        dataIndex: 'F910_31',
        key: 'F910_31',
        width: 80,
        align: 'right',
        fixed: 'right',
        render: (v: number) => (v != null ? v.toFixed(1) : ''),
      },
      {
        title: '出勤天數',
        dataIndex: 'F910_32',
        key: 'F910_32',
        width: 80,
        align: 'right',
        fixed: 'right',
      },
    ];

    return [...fixedCols, ...dayCols, ...summaryCols];
  }, [daysInMonth, queryYear, queryMonth]);

  if (queried && noData) {
    return <Empty description="查無此資料" style={{ marginTop: 40 }} />;
  }

  if (!queried) {
    return null;
  }

  // Calculate total scroll width: fixed(330) + days*50 + summary(160)
  const scrollX = 330 + daysInMonth * 50 + 160;

  return (
    <Table<AttendanceRow>
      columns={columns}
      dataSource={data}
      rowKey={(r, i) => `${r.F910_18}-${r.F910_01}-${i}`}
      loading={loading}
      size="small"
      pagination={{
        pageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: ['20', '50', '100', '200'],
        showTotal: (total) => `共 ${total} 筆`,
      }}
      scroll={{ x: scrollX }}
    />
  );
});

ResultTable.displayName = 'ResultTable';

export default ResultTable;
